import type { ReactNode } from "react";
import { cn } from "@web/lib/cn";
import { linkMark, linkSuffix, useEmailLinks } from "./links";
import { parseBlocks, parseInline, type Inline } from "./markdownLite";

/**
 * A link Claude wrote: its host follows it ("example.com ↗"), and on a card whose email doesn't
 * contain it, a calm "not in the email" says so before anyone clicks.
 */
export function ModelLink({ href, label }: { href: string; label: ReactNode }) {
  const inEmail = useEmailLinks();
  const mark = linkMark(href, inEmail);
  const text = typeof label === "string" ? label : href;
  return (
    <>
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        title={href}
        data-link-mark={mark}
        className="break-words text-accent-text underline decoration-accent/40 underline-offset-2 hover:decoration-accent"
      >
        {label}
      </a>
      <span
        className={cn("ml-1 text-xs", mark === "not_in_email" ? "text-note-ink" : "text-muted")}
      >
        {linkSuffix(text, href, mark)}
      </span>
    </>
  );
}

function renderInline(nodes: readonly Inline[]): ReactNode[] {
  return nodes.map((node, i) => {
    switch (node.t) {
      case "text":
        return <span key={i}>{node.v}</span>;
      case "br":
        return <br key={i} />;
      case "strong":
        return (
          <strong key={i} className="font-semibold text-ink">
            {node.v}
          </strong>
        );
      case "em":
        return <em key={i}>{node.v}</em>;
      case "code":
        return (
          <code key={i} className="rounded bg-sunk px-1 py-px font-mono text-[0.85em] text-ink-2">
            {node.v}
          </code>
        );
      case "link":
        return <ModelLink key={i} href={node.href} label={node.v} />;
    }
  });
}

/** One line of Markdown-lite (titles, list items, reasons). */
export function InlineText({ text }: { text: string | null | undefined }) {
  return <>{renderInline(parseInline(text ?? ""))}</>;
}

/** Card text: paragraphs, lists, safe links, emphasis. Never HTML. */
export function FormattedText({
  text,
  className,
}: {
  text: string | null | undefined;
  className?: string;
}) {
  const blocks = parseBlocks(text);
  if (!blocks.length) return null;
  return (
    <div className={cn("flex flex-col gap-2 break-words", className)}>
      {blocks.map((block, i) => {
        if (block.t === "p") return <p key={i}>{renderInline(block.inline)}</p>;
        const items = block.items.map((item, j) => <li key={j}>{renderInline(item)}</li>);
        return block.t === "ul" ? (
          <ul key={i} className="flex list-disc flex-col gap-1 pl-5 marker:text-faint">
            {items}
          </ul>
        ) : (
          <ol key={i} className="flex list-decimal flex-col gap-1 pl-5 marker:text-faint">
            {items}
          </ol>
        );
      })}
    </div>
  );
}
